import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  readUnitsRequest,
  sendPushRequest,
  sendPushOneSignalRequest,
  updateTestAlarmsRequest,
  getAccountStatusRequest,
} from "../../services/APIService";
import { Units } from "../../model/UnitsModel";

interface SelectedUnit {
  ID: number;
  Unit: string;
  checked: boolean;
}

interface Props {
  loggedID: string;
  testAlarms: number;
  tag: string;
  setloggedState: React.Dispatch<React.SetStateAction<number>>;
}

export const MainPageViewModel = ({ loggedID, testAlarms, tag, setloggedState }: Props) => {
  const navigate = useNavigate();

  const [keyword, setKeyword] = useState("");
  const [street, setStreet] = useState("");
  const [streetNum, setStreetNum] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [place, setPlace] = useState("");
  const [selection, setSelection] = useState("Adresse");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [units, setUnits] = useState<Units[]>([]);
  const [selectedUnits, setSelectedUnits] = useState<SelectedUnit[]>([]);
  const [showPopUp, setShowPopup] = useState(false);
  const [useAlarmMonitor, setUseAlarmMonitor] = useState(false);
  
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [accountStatus, setAccountStatus] = useState<string>("");
  const [isExpired, setIsExpired] = useState<boolean>(false);

  const checkIsExpired = (status: string) => {
    if (status === "Test") return true;
    if (status === "Payed") return false;
    if (!status) return false;

    const today = new Date();
    const expirationDate = new Date(status);
    const daysBeforeWarning = 7;
    const warningWindow = daysBeforeWarning * 24 * 60 * 60 * 1000;

    return expirationDate.getTime() - today.getTime() <= warningWindow;
  };

  const loadAccountStatus = useCallback(async () => {
    if (!tag) return;
    try {
      const data = await getAccountStatusRequest(tag);
      if (data && data.accountStatus) {
        setAccountStatus(data.accountStatus);
        setIsExpired(checkIsExpired(data.accountStatus));
      }
    } catch (error) {
      console.error("Fehler beim Laden des Account-Status:", error);
    }
  }, [tag]);

  const refreshUnits = useCallback(async () => {
    if (!loggedID) return;
    try {
      const data = await readUnitsRequest(loggedID);
      if (Array.isArray(data)) {
        setUnits(data);
        const newSelectedUnits = data.map((e: Units) => ({
          ID: e.ID,
          Unit: e.Unit,
          checked: false,
        }));
        setSelectedUnits(newSelectedUnits);
      }
    } catch (error) {
      console.error("Fehler beim Laden der Einheiten:", error);
    }
  }, [loggedID]);

  useEffect(() => {
    refreshUnits();
    loadAccountStatus();
  }, [refreshUnits, loadAccountStatus]);

  const sendAlarm = async () => {
    let finalStreet = street;
    let finalStreetNum = streetNum;
    let finalPostalCode = postalCode;
    let finalPlace = place;
    let finalLatitude = latitude;
    let finalLongitude = longitude;

    // 1. Wenn "Koordinate" ausgewählt ist (Reverse Geocoding)
    if (selection === "Koordinate") {
      if (!longitude || !latitude) {
        alert("Bitte geben Sie sowohl den Längengrad als auch den Breitengrad ein.");
        return;
      }

      try {
        const manLatitude = (parseFloat(latitude.replace(",", ".")) + 0.0000442).toFixed(7);
        const manLongitude = (parseFloat(longitude.replace(",", ".")) + 0.0001073).toFixed(7);

        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${manLatitude}&lon=${manLongitude}&addressdetails=1`
        );
        const data = await response.json();

        if (!data || (!data.address && !data.display_name)) {
          alert("Für die Koordinate existiert keine Adresse und der Alarm ist nicht auslösbar.");
          return;
        }

        finalStreet = data.address?.road || "";
        finalStreetNum = data.address?.house_number || "";
        finalPostalCode = data.address?.postcode || "";
        finalPlace = data.address?.village || data.address?.city || data.address?.town || "";
        finalLatitude = manLatitude;
        finalLongitude = manLongitude;

        setStreet(finalStreet);
        setStreetNum(finalStreetNum);
        setPostalCode(finalPostalCode);
        setPlace(finalPlace);

      } catch (error) {
        console.error("Fehler beim Abrufen der Adresse für die Koordinate:", error);
        alert("Es gab einen Fehler beim Abrufen der Adresse. Der Alarm wurde nicht gesendet.");
        return;
      }
    } 
    // 2. NEU: Wenn "Adresse" ausgewählt ist (Forward Geocoding)
    else if (selection === "Adresse") {
      if (!street && !place) {
        alert("Bitte geben Sie mindestens eine Straße oder einen Ort ein.");
        return;
      }

      try {
        const query = `${street} ${streetNum}, ${postalCode} ${place}`.trim();
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`
        );
        const data = await response.json();

        if (!data || data.length === 0) {
          alert("Für die eingegebene Adresse konnten keine Koordinaten ermittelt werden.");
          return;
        }

        finalLatitude = data[0].lat;
        finalLongitude = data[0].lon;

        setLatitude(finalLatitude);
        setLongitude(finalLongitude);

      } catch (error) {
        console.error("Fehler beim Abrufen der Koordinaten für die Adresse:", error);
        alert("Es gab einen Fehler beim Abrufen der Koordinaten.");
        return;
      }
    }

    try {
      const payload = {
        keyword,
        street: finalStreet,
        streetNum: finalStreetNum,
        postalCode: finalPostalCode,
        place: finalPlace,
        tag,
        longitude: finalLongitude,
        latitude: finalLatitude,
      };

      await updateTestAlarmsRequest(tag, testAlarms - 1);

      const unitsToSend = selectedUnits
        .filter((u) => u.checked)
        .map((u) => ({ Unit: u.Unit }));

      await sendPushOneSignalRequest({ ...payload, unitsToSend });
      await sendPushRequest(payload);
    } catch (error) {
      console.error("Fehler beim Senden des Alarms:", error);
    } finally {
      // Entkopplung durch setTimeout, damit die API-Calls und Events sauber beendet werden, 
      // bevor der State zurückgesetzt und umgeleitet wird.
      setTimeout(() => {
        setloggedState(0);
        navigate("/");
      }, 100);
    }
  };

  const handleLogout = () => {
    setloggedState(0);
    navigate("/");
  };

  const openSettings = () => setShowSettingsModal(true);
  const closeSettings = async () => {
    setShowSettingsModal(false);
    await refreshUnits();
    await loadAccountStatus();
  };

  const openPayModal = () => setShowPayModal(true);
  const closePayModal = async () => {
    setShowPayModal(false);
    await loadAccountStatus();
  };

  return {
    state: {
      keyword,
      street,
      streetNum,
      postalCode,
      place,
      selection,
      latitude,
      longitude,
      units,
      selectedUnits,
      showPopUp,
      useAlarmMonitor,
      showSettingsModal,
      showPayModal,
      accountStatus,
      isExpired,
    },
    actions: {
      setKeyword,
      setStreet,
      setStreetNum,
      setPostalCode,
      setPlace,
      setSelection,
      setLatitude,
      setLongitude,
      setSelectedUnits,
      setUseAlarmMonitor,
      setShowPopup,
      sendAlarm,
      openSettings,
      closeSettings,
      openPayModal,
      closePayModal,
      refreshUnits,
      handleLogout,
    },
  };
};